import { Box } from "@chakra-ui/react";
import { codeBenchActions, objectUtils } from '@codametrix/ui-common';
import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Action } from 'redux';
import { CaseNoteCard } from './CaseNoteCard';
import './case-notes-panel-body.scss';
import type { NoteEditState } from './case-notes-panel-body.types';

export type CaseNotesPanelBodyProps = {
  lookupParams: AmplifyAPI.CoderNoteInputParams;
};

export const CaseNotesPanelBody: React.FC<CaseNotesPanelBodyProps> = ({
  lookupParams
}) => {
  const { coderNotesList, activeUser } = useSelector(
    (state: CMx.ApplicationState) => state.coderNotes
  );
  const orderedNotes = useMemo(
    () =>
      (coderNotesList ?? [])
        .slice()
        .sort((a, b) =>
          (b.created_date ?? '').localeCompare(a.created_date ?? '')
        ),
    [coderNotesList]
  );
  const dispatch = useDispatch();
  const [coderNoteContent, setCoderNoteContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editMode, setEditMode] = useState<NoteEditState>({
    noteId: '',
    isEditEnabled: false
  });
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const toggleEditMode = (toggle: boolean, noteId?: string) => {
    setEditMode({ noteId: noteId ?? '', isEditEnabled: toggle });
  };

  const handleEditNote = (note: AmplifyAPI.CoderNoteRow) => {
    dispatch(
      codeBenchActions.updateCoderNote({
        ...lookupParams,
        noteId: note.note_id,
        noteContent: note.note_content
      }) as any as Action
    );
    toggleEditMode(false);
  };

  const handleDeleteNote = (note: AmplifyAPI.CoderNoteRow) => {
    dispatch(
      codeBenchActions.deleteCoderNote({
        ...lookupParams,
        noteId: note.note_id
      }) as any as Action
    );
  };

  const handleCoderNoteChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setCoderNoteContent(e.target.value);
  };

  const canAdd =
    !objectUtils.isEmpty(coderNoteContent) &&
    !isSubmitting &&
    !editMode.isEditEnabled;

  // Guard against a double-submit (double-click, or the ⌘+Enter shortcut and the button both
  // firing for one logical action): without this, two overlapping dispatches each POST a real
  // note, and the list reducer — which correctly replaces `coderNotesList` wholesale on refetch —
  // then faithfully renders both as a "duplicated" note.
  const addNote = async () => {
    if (!canAdd) {
      return;
    }
    setIsSubmitting(true);
    try {
      await (dispatch(
        codeBenchActions.addCoderNote({
          ...lookupParams,
          noteContent: coderNoteContent
        }) as any as Action
      ) as unknown as Promise<unknown>);
      setCoderNoteContent('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComposeKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      addNote();
    }
  };

  return (
    <div className="case-notes-panel-body" data-testid="case-notes-panel-body">
      <div className="ca-note-compose">
        <textarea
          ref={textareaRef}
          className="ca-note-compose__input"
          data-testid="add-note-textarea"
          value={coderNoteContent}
          placeholder="Add a note for the team — visible to anyone reviewing this case…"
          onChange={handleCoderNoteChange}
          onKeyDown={handleComposeKeyDown}
          disabled={isSubmitting || editMode.isEditEnabled}
        />
        <div className="ca-compose-row">
          <span className="ca-hint">⌘ + Enter to add</span>
          <button
            type="button"
            className="ca-note-add-btn"
            onClick={addNote}
            disabled={isSubmitting || editMode.isEditEnabled}
          >
            {isSubmitting ? 'Adding…' : 'Add note'}
          </button>
        </div>
      </div>
      <div className="case-notes-panel-body__list">
        {orderedNotes.length ? (
          <Box style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
            {
              orderedNotes.map((note) => (
                <CaseNoteCard
                  key={note.note_id}
                  note={note}
                  activeUser={activeUser}
                  editMode={editMode}
                  toggleEditMode={toggleEditMode}
                  onEdit={handleEditNote}
                  onDelete={handleDeleteNote}
                />
              ))
            }
          </Box>
        ) : (
          <div className="case-notes-panel-body__empty" data-testid="case-notes-empty">
            No notes yet on this case.
          </div>
        )}
      </div>
    </div>
  );
};

CaseNotesPanelBody.displayName = 'CaseNotesPanelBody';
